import { skipToken, useSuspenseQueries } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    ActionableContainerSkeleton,
    Alert,
    Button,
    Heading,
    Panel,
    PanelBody,
    PanelSet,
    PanelSetContext,
} from '@gitbook/spine-react';
import { ErrorBoundary } from '@gitbook/util-debug';
import { assertIsDefined } from '@gitbook/util-ts';

import {
    assertIsExtendedSiteInstallation,
    assertIsExtendedSpaceInstallation,
    getSiteTitle,
    IntegrationConfigureOrgFlow,
    IntegrationConfigureSiteFlow,
    IntegrationConfigureSpaceFlow,
    IntegrationStepWrapper,
    SpaceIcon,
} from '../../../../Components';
import { useSetAnalyticsScreen } from '../../../../navigation';
import { apiQuery } from '../../../../state';
import { IntegrationsEmptyState } from '../../components';
import { getContentDataFromInstallationContext } from '../../integrations.utils';
import type { IntegrationInstallationFlowProps, StepContextType } from '../types';
import { isConfigurationStatusKnowable } from '../utils';

type ConfigurationStepProps = IntegrationInstallationFlowProps & {
    context: StepContextType<'configure_content_installation'>;
    onNext: () => void;
};

/**
 * Step to configure the integration for a content context.
 */
export function ConfigurationStep(props: ConfigurationStepProps) {
    const { integration, installation, organizationId, context, onNext } = props;

    const contentTitle =
        context.type === 'site'
            ? getSiteTitle(context.installation.site.title)
            : context.installation.space.title;

    const { id: contentId } = getContentDataFromInstallationContext(context);

    // although we have the installation here we need to fetch again to subscribe to the
    // configuration changes to detemine the state of the configuration
    const [{ data: siteInstallation }, { data: spaceInstallation }] = useSuspenseQueries({
        queries: [
            apiQuery(
                'getIntegrationSiteInstallation',
                context.type === 'site'
                    ? {
                          installationId: installation.id,
                          siteId: contentId,
                          integrationName: integration.name,
                          extended: true,
                      }
                    : skipToken
            ),
            apiQuery(
                'getIntegrationSpaceInstallation',
                context.type === 'space'
                    ? {
                          installationId: installation.id,
                          spaceId: contentId,
                          integrationName: integration.name,
                          extended: true,
                      }
                    : skipToken
            ),
        ],
    });

    const updatedContext: StepContextType<'configure_content_installation'> = (() => {
        switch (context.type) {
            case 'site': {
                assertIsDefined(siteInstallation);
                assertIsExtendedSiteInstallation(siteInstallation);
                return {
                    type: 'site',
                    installation: siteInstallation,
                };
            }
            case 'space': {
                assertIsDefined(spaceInstallation);
                assertIsExtendedSpaceInstallation(spaceInstallation);
                return {
                    type: 'space',
                    installation: spaceInstallation,
                };
            }
        }
    })();

    const isConfigStateKnowable = isConfigurationStatusKnowable(integration, updatedContext);

    useSetAnalyticsScreen({
        screen: 'integration_installation',
        step: 'configure_content_installation',
        integration: integration.name,
        organization: organizationId,
        context: context.type,
        withAccountInstallation: !!installation,
        withContentInstallation: !!siteInstallation || !!spaceInstallation,
    });

    return (
        <div className="h-full overflow-y-auto">
            <IntegrationStepWrapper
                title={`Configure ${context.type}`}
                description={
                    updatedContext.installation.status === api.IntegrationInstallationStatus.Active
                        ? `Integration is configured and working in the ${context.type} ${contentTitle}.`
                        : `Configure the integration to start using it on the ${context.type} ${contentTitle}.`
                }
                content={
                    contentTitle
                        ? {
                              title: contentTitle,
                              icon:
                                  updatedContext.type === 'space' ? (
                                      <SpaceIcon
                                          space={updatedContext.installation.space}
                                          size={24}
                                      />
                                  ) : (
                                      <Icon.DocsSite />
                                  ),
                          }
                        : undefined
                }
                status={updatedContext.installation.status}
            >
                <ErrorBoundary fallback={<IntegrationsEmptyState kind="no-installation" />}>
                    <React.Suspense fallback={<ActionableContainerSkeleton />}>
                        <ConfigurationFlow
                            integration={integration}
                            orgInstallation={installation}
                            spaceInstallation={spaceInstallation}
                            siteInstallation={siteInstallation}
                            onNext={onNext}
                        />
                        {isConfigStateKnowable ? (
                            <Button
                                label="Done"
                                kind="primary"
                                onPress={onNext}
                                className="mt-auto self-end"
                            />
                        ) : null}
                    </React.Suspense>
                </ErrorBoundary>
            </IntegrationStepWrapper>
        </div>
    );
}

/**
 * Handles the configuration "black-box"
 */
function ConfigurationFlow(props: {
    integration: api.Integration;
    orgInstallation: api.IntegrationInstallation;
    spaceInstallation: api.IntegrationSpaceInstallation | null;
    siteInstallation: api.IntegrationSiteInstallation | null;
    onNext: () => void;
}) {
    const { integration, orgInstallation, spaceInstallation, siteInstallation, onNext } = props;

    const configurationLevels = Object.keys(integration.configurations ?? {}).filter(
        (key): key is keyof api.IntegrationConfigurations =>
            ['account', 'space', 'site'].includes(key)
    );

    if (configurationLevels.length === 0) {
        return <Alert kind="info" title="This integration does not need any configuration" />;
    }

    function getConfigurationFlow(configLevel: keyof api.IntegrationConfigurations) {
        switch (configLevel) {
            case 'account':
                return (
                    <IntegrationConfigureOrgFlow
                        integration={integration}
                        installation={orgInstallation}
                        onComplete={onNext}
                    />
                );
            case 'space': {
                if (!spaceInstallation) {
                    return <IntegrationsEmptyState kind="no-installation" />;
                }

                return (
                    <IntegrationConfigureSpaceFlow
                        integration={integration}
                        spaceInstallation={spaceInstallation}
                        onComplete={onNext}
                    />
                );
            }
            case 'site': {
                if (!siteInstallation) {
                    return <IntegrationsEmptyState kind="no-installation" />;
                }

                return (
                    <IntegrationConfigureSiteFlow
                        integration={integration}
                        siteInstallation={siteInstallation}
                        onComplete={onNext}
                    />
                );
            }
        }
    }

    return (
        <PanelSetContext.Provider value={{ variant: 'enclosed' }}>
            <PanelSet>
                {integration.configurations?.account ? (
                    <Panel>
                        <Heading>Organization configuration</Heading>
                        <PanelBody>{getConfigurationFlow('account')}</PanelBody>
                    </Panel>
                ) : null}
                {spaceInstallation && integration.configurations?.space ? (
                    <Panel>
                        <Heading>Space configuration</Heading>
                        <PanelBody>{getConfigurationFlow('space')}</PanelBody>
                    </Panel>
                ) : siteInstallation && integration.configurations?.site ? (
                    <Panel>
                        <Heading>Site configuration</Heading>
                        <PanelBody>{getConfigurationFlow('site')}</PanelBody>
                    </Panel>
                ) : null}
            </PanelSet>
        </PanelSetContext.Provider>
    );
}
